import { useEffect, useState } from 'react';
import { supabase } from '../lib/supabase';
import { bakeCroppedVideo, probeVideo, renderVideoCoverJpeg, sampleVideoFrames, videoOutputSize } from '../lib/bakeCroppedVideo';
import { invokeErrorMessage } from '../lib/supabaseFunctionError';
import { uploadMedia } from '../lib/mediaLibrary';
import { clampCaptionSize, defaultVideoCaption, hasCaptionText, type VideoCaption } from '../lib/videoCaption';

/**
 * A marketing bot can stage a video the browser still has to finish: drawing
 * text onto the file, and choosing a cover frame when the owner did not name
 * one. The cron holds the post until those flags are cleared. The first time
 * the post is shown in the app, this hook does the work.
 */

export type FinishVideoCaptionState =
  | { status: 'idle' }
  | { status: 'baking'; progress: number }
  | { status: 'done'; mediaUrl: string }
  | { status: 'error'; message: string };

type Job = {
  promise: Promise<string>;
  progress: number;
  listeners: Set<(state: FinishVideoCaptionState) => void>;
};

// One bake per draft per page, even when the card and the preview modal are
// both on screen, or the card re-renders while encoding.
const jobs = new Map<string, Job>();
const finished = new Map<string, string>();

function readCaption(raw: unknown): VideoCaption | null {
  if (!raw || typeof raw !== 'object') return null;
  const row = raw as Record<string, unknown>;
  const base = defaultVideoCaption(typeof row.text === 'string' ? row.text : '');
  const caption: VideoCaption = {
    ...base,
    style: (typeof row.style === 'string' ? row.style : base.style) as VideoCaption['style'],
    color: typeof row.color === 'string' && /^#[0-9a-f]{6}$/i.test(row.color) ? row.color : base.color,
    x: typeof row.x === 'number' ? Math.min(1, Math.max(0, row.x)) : base.x,
    y: typeof row.y === 'number' ? Math.min(1, Math.max(0, row.y)) : base.y,
    size: typeof row.size === 'number' ? clampCaptionSize(row.size) : base.size,
  };
  return hasCaptionText(caption) ? caption : null;
}

async function postedAspect(
  format: string,
  width: number,
  height: number,
  url: string,
): Promise<number> {
  if (format !== 'feed') return 9 / 16;
  if (width > 0 && height > 0) return width / height;
  const probed = await probeVideo(url);
  return probed.width > 0 && probed.height > 0 ? probed.width / probed.height : 1;
}

/** Ask the vision model which sample is the best cover. Falls back to the middle frame. */
async function chooseCoverIndex(businessId: string, frames: string[]): Promise<number> {
  const middle = Math.floor((frames.length - 1) / 2);
  try {
    const { data, error } = await supabase.functions.invoke('social-auto-post-action', {
      body: { action: 'pick_video_cover', businessId, frames },
    });
    if (error || data?.error) return middle;
    const index = Number(data?.index);
    if (!Number.isInteger(index) || index < 0 || index >= frames.length) return middle;
    return index;
  } catch {
    return middle;
  }
}

async function runBake(businessId: string, draftId: string, job: Job): Promise<string> {
  const notify = (state: FinishVideoCaptionState) => job.listeners.forEach((fn) => fn(state));

  const { data: draft, error } = await supabase
    .from('social_post_drafts')
    .select('id, media_id, metadata, status')
    .eq('id', draftId)
    .maybeSingle();
  if (error) throw new Error(error.message);
  if (!draft || draft.status !== 'pending') throw new Error('This post is no longer staged.');
  const meta = (draft.metadata && typeof draft.metadata === 'object' ? draft.metadata : {}) as Record<string, unknown>;
  const captionPending = meta.videoCaptionPending === true;
  const thumbPending = meta.videoThumbnailPending === true;
  if (!captionPending && !thumbPending) {
    const { data: current } = draft.media_id
      ? await supabase.from('media_library').select('url').eq('id', draft.media_id).maybeSingle()
      : { data: null };
    return String(current?.url || '');
  }
  if (!draft.media_id) throw new Error('The video for this post is missing.');

  const { data: media } = await supabase
    .from('media_library')
    .select('id, url, width, height')
    .eq('id', draft.media_id)
    .maybeSingle();
  if (!media?.url) throw new Error('The video for this post is missing.');

  const format = String(meta.postFormat || 'feed').toLowerCase();
  const width = Number(media.width) || 0;
  const height = Number(media.height) || 0;
  let videoUrl = String(media.url);
  notify({ status: 'baking', progress: 0 });

  // Pick the moment before any re-encode, so the timestamp still matches the
  // file that gets published. A named photo skips the frame search.
  let coverTime = 0;
  let photoUrl: string | null = null;
  if (thumbPending) {
    const photoId = typeof meta.videoThumbnailPhotoId === 'string' ? meta.videoThumbnailPhotoId : '';
    if (photoId) {
      const { data: photo } = await supabase
        .from('media_library')
        .select('url')
        .eq('id', photoId)
        .maybeSingle();
      photoUrl = photo?.url ? String(photo.url) : null;
    }
    if (!photoUrl) {
      const samples = await sampleVideoFrames(videoUrl);
      if (samples.length > 0) {
        const index = await chooseCoverIndex(businessId, samples.map((frame) => frame.dataUrl));
        coverTime = samples[Math.min(index, samples.length - 1)]?.time ?? samples[0].time;
      }
    }
  }

  if (captionPending) {
    const caption = readCaption(meta.videoCaption);
    if (!caption) throw new Error('This post has no video text to add.');
    const aspect = await postedAspect(format, width, height, videoUrl);
    const blob = await bakeCroppedVideo({
      url: videoUrl,
      croppedAreaPixels: null,
      aspect,
      caption,
      onProgress: (ratio) => {
        job.progress = ratio;
        notify({ status: 'baking', progress: ratio });
      },
    });
    const [uploaded] = await uploadMedia(
      businessId,
      [new File([blob], `post-video-${draftId.slice(0, 8)}.mp4`, { type: 'video/mp4' })],
      { source: 'social_crop' },
    );
    if (!uploaded) throw new Error('Could not save the finished video.');
    const { data, error: finishError } = await supabase.functions.invoke('social-auto-post-action', {
      body: { action: 'finish_video_caption', businessId, draftId, mediaId: uploaded.id },
    });
    if (finishError || data?.error) {
      throw new Error(data?.error || (await invokeErrorMessage(finishError, data)) || 'Could not finish the video.');
    }
    videoUrl = String(data?.mediaUrl || uploaded.url);
  }

  if (thumbPending) {
    const aspect = await postedAspect(format, width, height, videoUrl);
    const outputSize = captionPending
      ? videoOutputSize(aspect)
      : width > 0 && height > 0
        ? { width, height }
        : null;
    const cover = await renderVideoCoverJpeg({
      aspect,
      outputSize,
      videoUrl,
      atSeconds: coverTime,
      photoUrl,
    });
    const [coverRow] = await uploadMedia(
      businessId,
      [new File([cover], 'video-cover.jpg', { type: 'image/jpeg' })],
      { source: 'social_crop' },
    );
    if (!coverRow?.url) throw new Error('Could not save the cover image.');
    const { data, error: coverError } = await supabase.functions.invoke('social-auto-post-action', {
      body: { action: 'finish_video_thumbnail', businessId, draftId, mediaId: coverRow.id },
    });
    if (coverError || data?.error) {
      throw new Error(data?.error || (await invokeErrorMessage(coverError, data)) || 'Could not save the cover.');
    }
  }

  return videoUrl;
}

/** Start the bake/cover job if it is not already running. Safe to call from a list. */
export function requestVideoFinish(businessId: string, draftId: string): void {
  if (!businessId || !draftId || finished.has(draftId)) return;
  startJob(businessId, draftId).promise.catch(() => undefined);
}

function startJob(businessId: string, draftId: string): Job {
  const existing = jobs.get(draftId);
  if (existing) return existing;
  const job: Job = { promise: Promise.resolve(''), progress: 0, listeners: new Set() };
  job.promise = runBake(businessId, draftId, job)
    .then((url) => {
      finished.set(draftId, url);
      job.listeners.forEach((fn) => fn({ status: 'done', mediaUrl: url }));
      return url;
    })
    .catch((err: unknown) => {
      const message = err instanceof Error ? err.message : 'Could not finish this video.';
      job.listeners.forEach((fn) => fn({ status: 'error', message }));
      throw err;
    })
    .finally(() => {
      jobs.delete(draftId);
    });
  jobs.set(draftId, job);
  return job;
}

/**
 * Finish the on-video text for a staged social post when `pending` is true.
 * Returns the current state so the caller can show progress and the new file.
 */
export function useFinishVideoCaption(options: {
  businessId: string | null | undefined;
  draftId: string | null | undefined;
  pending: boolean;
}): FinishVideoCaptionState {
  const { businessId, draftId, pending } = options;
  const [state, setState] = useState<FinishVideoCaptionState>(() => {
    const url = draftId ? finished.get(draftId) : undefined;
    return url ? { status: 'done', mediaUrl: url } : { status: 'idle' };
  });

  useEffect(() => {
    if (!businessId || !draftId || !pending) return;
    const url = finished.get(draftId);
    if (url) {
      setState({ status: 'done', mediaUrl: url });
      return;
    }
    const job = startJob(businessId, draftId);
    const listener = (next: FinishVideoCaptionState) => setState(next);
    job.listeners.add(listener);
    const already = finished.get(draftId);
    setState(already ? { status: 'done', mediaUrl: already } : { status: 'baking', progress: job.progress });
    job.promise.catch(() => undefined);
    return () => {
      job.listeners.delete(listener);
    };
  }, [businessId, draftId, pending]);

  return state;
}
